import { ChangeEvent, memo, ReactElement, ReactNode, useCallback, useMemo } from 'react';
import { BaseFilterProps } from './common';
import { BooleanFilterDTO, FilterBooleanOptionDTO } from '../../../api/generated';
import FormControl from '@mui/material/FormControl';
import { Box, FormControlLabel, List, ListItem, Radio, RadioGroup, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

export interface BinaryChoiceFilterProps extends BaseFilterProps<BooleanFilterDTO, FilterBooleanOptionDTO> {
  filterKey?: string;
  trueOptionIcon?: ReactNode;
  falseOptionIcon?: ReactNode;
}

export const BinaryChoiceFilter = memo(function ({
  filterKey,
  value,
  onValueChange,
  onCommit,
  filterOptions,
  trueOptionIcon = undefined,
  falseOptionIcon = undefined,
}: BinaryChoiceFilterProps): ReactElement {
  const isDisabled = useMemo(() => !filterOptions, [filterOptions]);

  const handleChange = useCallback(
    function (event: ChangeEvent<HTMLInputElement>, newValue: string): void {
      const parsed = stringToBool(newValue);
      if (parsed === undefined) {
        onValueChange(undefined);
      } else {
        onValueChange({ eq: parsed });
      }
      onCommit();
    },
    [onValueChange, onCommit]
  );

  return (
    <FormControl
      component='fieldset'
      sx={{
        marginTop: '-1rem',
        maxHeight: '200px',
        overflow: 'auto',
        paddingLeft: '4rem',
        width: '90%',
      }}
      disabled={isDisabled}
    >
      <RadioGroup value={value?.eq || ''} onChange={handleChange}>
        <List>
          {[true, false].map((tf) => (
            <ListItem key={`${tf}`} disablePadding disableGutters>
              <FormControlLabel
                sx={{ width: '100%' }}
                value={`${tf}`}
                disableTypography
                label={
                  <Box display='flex' flexDirection='row' alignItems='center' flex='1' gap='0.5em'>
                    <BinaryChoice filterKey={filterKey ?? ''} icon={tf ? trueOptionIcon : falseOptionIcon} value={tf} />
                    <Typography noWrap textAlign='right'>
                      {tf ? (filterOptions?.trueCount ?? '-') : (filterOptions?.falseCount ?? '-')}
                    </Typography>
                  </Box>
                }
                control={<Radio checked={tf === value?.eq} />}
              />
            </ListItem>
          ))}
        </List>
      </RadioGroup>
    </FormControl>
  );
});

function stringToBool(value: string): boolean | undefined {
  return { true: true, false: false }[value];
}

type BinaryChoiceProps = {
  filterKey: string;
  icon?: ReactNode;
  value?: boolean;
};

const BinaryChoice = memo(function BinaryChoice({ filterKey, icon, value }: BinaryChoiceProps) {
  const { t } = useTranslation();

  const filterValue = `filter.${filterKey}.${value}`;
  const genericValue = `common.${value ? 'yes' : 'no'}`;

  return (
    <>
      {icon}
      <Typography flex={1}>{t([filterValue, genericValue])}</Typography>
    </>
  );
});
